/**
 * DocuVaultUpgradePlans
 * ─────────────────────
 * Upgrade modal for DocuVault storage plans.
 * ★ Uses convertAmount(price_usd, 'USD') before formatCurrency so every
 *   price is correctly converted to the user's selected currency.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useCurrency } from '../../context/CurrencyContext';
import { formatStorage, activatePlan, loadStorageTiers, STORAGE_TIERS } from '../../services/subscriptionService';

const { FiX, FiCheck, FiZap, FiStar, FiShield, FiLoader, FiRefreshCw, FiAlertTriangle, FiGlobe, FiTrendingUp } = FiIcons;

// ── PlanCard ──────────────────────────────────────────────────────────────────
const PlanCard = ({ tier, isCurrent, onSelect, loading, fmtPrice, currency, billing }) => {
  const isNonUSD = currency !== 'USD';
  const isMonthlyPlan = tier.billing_cycle === 'month';
  const displayPrice = (isMonthlyPlan && billing === 'annual') ? tier.price_usd * 0.8 : tier.price_usd;

  return (
    <motion.div layout
      className={`relative rounded-[28px] border-2 p-6 flex flex-col gap-4 transition-all ${
        tier.highlight
          ? 'border-accent-gold shadow-xl shadow-accent-gold/15'
          : isCurrent
            ? 'border-accent-navy bg-accent-navy/5'
            : 'border-brand-dark hover:border-accent-gold/50'
      }`}>
      {tier.highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-accent-gold text-accent-navy text-[9px] font-black uppercase tracking-widest rounded-full shadow-md">
          Most Popular
        </div>
      )}
      {isCurrent && (
        <div className="absolute -top-3 right-4 px-3 py-1 bg-accent-navy text-white text-[9px] font-black uppercase tracking-widest rounded-full">
          Current Plan
        </div>
      )}

      <div>
        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl border text-[9px] font-black uppercase tracking-widest mb-3 ${tier.badge || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
          <SafeIcon icon={tier.plan_key === 'free' ? FiShield : tier.plan_key === 'legacy' ? FiStar : FiZap} className="w-3 h-3" />
          {tier.name}
        </div>

        {/* ★ Converted price */}
        <p className="text-3xl font-serif font-bold text-accent-navy">
          {tier.price_usd === 0 ? 'Free' : fmtPrice(displayPrice)}
          {tier.price_usd > 0 && (
            <span className="text-sm font-light text-text-muted">
              /{tier.billing_cycle === 'year' ? 'yr' : tier.billing_cycle === 'forever' ? '' : 'mo'}
            </span>
          )}
        </p>
        
        {/* Annual savings hint */}
        {isMonthlyPlan && billing === 'annual' && tier.price_usd > 0 && (
          <div className="text-[9px] font-bold text-green-600 mt-1 uppercase tracking-widest flex items-center gap-1">
            <SafeIcon icon={FiTrendingUp} className="w-3 h-3" /> Save 20%
          </div>
        )}

        {/* ★ USD reference when non-USD */}
        {isNonUSD && displayPrice > 0 && (
          <p className="text-[9px] text-text-muted mt-1">${displayPrice.toFixed(2)} USD</p>
        )}
        <p className="text-sm font-bold text-accent-gold mt-2">{formatStorage(tier.storage_mb)} Storage</p>
      </div>

      <ul className="space-y-2 flex-1 mt-2">
        {(tier.features || []).map(f => (
          <li key={f} className="flex items-center gap-2 text-[11px] text-text-muted">
            <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiCheck} className="w-2.5 h-2.5 text-green-600" />
            </div>
            {f}
          </li>
        ))}
      </ul>

      <button onClick={() => onSelect(tier)} disabled={isCurrent || loading}
        className={`w-full py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
          isCurrent
            ? 'bg-brand-light text-text-muted cursor-default border border-brand-dark'
            : tier.highlight
              ? 'bg-accent-gold text-accent-navy hover:bg-accent-gold/90 shadow-lg shadow-accent-gold/20'
              : 'bg-accent-navy text-white hover:bg-accent-navy/90'
        }`}>
        {loading
          ? <><SafeIcon icon={FiLoader} className="w-3.5 h-3.5 animate-spin" /> Processing…</>
          : isCurrent ? 'Active Plan' : tier.price_usd === 0 ? 'Downgrade' : 'Upgrade Now'
        }
      </button>
    </motion.div>
  );
};

// ── DocuVaultUpgradePlans ─────────────────────────────────────────────────────
const DocuVaultUpgradePlans = ({ isOpen, onClose, currentPlanId, onPlanChanged }) => {
  // ★ Pull convertAmount for correct USD → local conversion
  const { formatCurrency, convertAmount, currency, getExchangeRate, ratesReady } = useCurrency();

  const [tiers,     setTiers]     = useState(STORAGE_TIERS);
  const [loadingId, setLoadingId] = useState(null);
  const [success,   setSuccess]   = useState(null);
  const [fetching,  setFetching]  = useState(false);
  const [fetchErr,  setFetchErr]  = useState(null);
  const [billing,   setBilling]   = useState('annual'); // Added billing toggle

  // ★ Centralised price formatter: convert then format
  const fmtPrice = useCallback((usd) => {
    if (usd === 0) return 'Free';
    return formatCurrency(convertAmount(usd, 'USD'));
  }, [formatCurrency, convertAmount]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setFetching(true);
    setFetchErr(null);
    loadStorageTiers()
      .then(fresh => { if (!cancelled) setTiers(fresh); })
      .catch(err  => { if (!cancelled) setFetchErr(err.message || 'Failed to load plans'); })
      .finally(()  => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  const handleRefresh = () => {
    setFetching(true);
    setFetchErr(null);
    loadStorageTiers()
      .then(fresh => setTiers(fresh))
      .catch(err => setFetchErr(err.message))
      .finally(() => setFetching(false));
  };

  const handleSelect = async (tier) => {
    if (tier.plan_key === currentPlanId || tier.id === currentPlanId) return;
    setLoadingId(tier.id);
    try {
      await activatePlan(tier);
      setSuccess(tier);
      setTimeout(() => { setSuccess(null); onPlanChanged?.(tier); onClose(); }, 2000);
    } catch (e) {
      console.error('[DocuVaultUpgradePlans] activatePlan error:', e);
    } finally {
      setLoadingId(null);
    }
  };

  if (!isOpen) return null;

  const isNonUSD = currency !== 'USD';
  const rate = getExchangeRate('USD', currency);
  const rateStr = rate >= 1000
    ? rate.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : rate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });

  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-accent-navy/70 backdrop-blur-md">
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col border border-brand-dark">

        {/* Header */}
        <div className="p-7 bg-gradient-to-r from-accent-navy to-[#1a2a5e] flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent-gold text-accent-navy rounded-2xl flex items-center justify-center shadow-lg">
              <SafeIcon icon={FiZap} className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-serif text-white">DocuVault Storage Plans</h3>
              <p className="text-white/50 text-[10px] font-black uppercase tracking-widest">
                Encrypted · AWS S3 · Live from Plans API
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleRefresh} disabled={fetching}
              className="p-2 text-white/60 hover:text-white rounded-xl transition-colors disabled:opacity-40" title="Refresh plans">
              <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${fetching ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onClose} className="p-2 text-white/60 hover:text-white rounded-xl transition-colors">
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-8">
          <AnimatePresence mode="wait">
            {success && (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center py-20 gap-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiCheck} className="w-10 h-10 text-green-500" />
                </div>
                <div className="text-center">
                  <p className="text-2xl font-serif text-accent-navy mb-2">Plan Activated!</p>
                  <p className="text-text-muted text-sm">
                    You're now on the <strong className="text-accent-gold">{success.name}</strong> plan
                    with <strong>{formatStorage(success.storage_mb)}</strong> of secure storage.
                  </p>
                </div>
              </motion.div>
            )}

            {!success && fetching && (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-24 gap-4">
                <div className="w-10 h-10 border-4 border-accent-gold/20 border-t-accent-gold rounded-full animate-spin" />
                <p className="text-text-muted text-sm font-bold uppercase tracking-widest">Loading plans…</p>
              </motion.div>
            )}

            {!success && !fetching && (
              <motion.div key="plans" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {fetchErr && (
                  <div className="mb-6 flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-2xl text-xs text-amber-700">
                    <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 flex-shrink-0" />
                    Showing cached plan data — live refresh failed. Storage limits may not reflect recent admin changes.
                  </div>
                )}

                <div className="text-center mb-6">
                  <h4 className="text-2xl font-serif text-accent-navy mb-2">Choose Your Storage Tier</h4>
                  <p className="text-text-muted text-sm max-w-md mx-auto">
                    All plans include AES-256 encryption, multi-party authorization, and direct AWS S3 transfers.
                    Upgrade anytime — storage increases instantly.
                  </p>
                  {/* ★ Currency conversion notice */}
                  {isNonUSD && (
                    <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full text-xs text-amber-700">
                      <SafeIcon icon={FiGlobe} className="w-3.5 h-3.5" />
                      Prices in {currency} · 1 USD = {rateStr} {currency} ·{' '}
                      <span className={`font-bold ${ratesReady ? 'text-green-700' : 'text-amber-700'}`}>
                        {ratesReady ? 'live rate' : 'est. rate'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Pricing Toggle */}
                <div className="flex justify-center mb-8">
                  <div className="flex items-center gap-2 bg-brand-light border border-brand-dark rounded-2xl p-1.5">
                    {['monthly', 'annual'].map(b => (
                      <button
                        key={b}
                        onClick={() => setBilling(b)}
                        className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                          billing === b
                            ? 'bg-accent-navy text-white shadow-lg shadow-accent-navy/20'
                            : 'text-text-muted hover:text-accent-navy'
                        }`}
                      >
                        {b === 'monthly' ? 'Monthly' : 'Annual · Save 20%'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {tiers.map(tier => (
                    <PlanCard
                      key={tier.id}
                      tier={tier}
                      isCurrent={tier.plan_key === currentPlanId || tier.id === currentPlanId}
                      onSelect={handleSelect}
                      loading={loadingId === tier.id}
                      fmtPrice={fmtPrice}
                      currency={currency}
                      billing={billing}
                    />
                  ))}
                </div>

                <p className="text-center text-[10px] text-text-muted mt-8">
                  All paid plans include a 7-day free trial. Cancel anytime.{' '}
                  {isNonUSD ? `Prices displayed in ${currency} — billed in USD.` : 'Prices in USD.'}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default DocuVaultUpgradePlans;